import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useCallback,
  Dispatch,
  SetStateAction,
} from "react";
import { ExtensionDetailsType, ExtensionFolder } from "../Types";
import {
  EA_TO_EV2_EXTENSION_MAPPINGS_KEY,
  EA_TO_EV2EXPERIMENT_MAPPING,
  EXTENSION_KEY_LOCAL_STORAGE,
} from "../Shared/shared.consts";

interface AppContextValue {
  error: string | null;
  setError: Dispatch<SetStateAction<string | null>>;
  extensionName: string;
  setExtensionName: (extensionName: string) => void;
  extensions: ExtensionFolder[];
  setExtensions: (extensionNames: ExtensionFolder[]) => void;
  extensionMappings: Record<string, string>;
  updateExtensionMapping: (fromExtension: string, toExtension: string) => void;
  getExtensionMapping: (extensionName: string) => string | undefined;
  getNormalizedExtensionName: (extensionName: string) => string;
  getNormalizedExtension: (
    extensionName: string,
  ) => ExtensionFolder | undefined;
  isExtensionNameValid: (extensionName: string) => boolean;
  extensionConfig: ExtensionDetailsType | null;
  setExtensionConfig: (config: ExtensionDetailsType) => void;
}

const AppContext = createContext<AppContextValue | undefined>(undefined);

// Provider props interface
interface AppProviderProps {
  children: ReactNode;
  initialExtensions?: ExtensionFolder[];
  extensionError?: string | null;
}

// Provider component
export const AppProvider: React.FC<AppProviderProps> = ({
  children,
  initialExtensions = [],
  extensionError = null,
}) => {
  const [error, setError] = useState<string | null>(extensionError);
  const [extensionName, setExtensionNameState] = useState("");
  const [extensions, setExtensions] =
    useState<ExtensionFolder[]>(initialExtensions);
  const [extensionMappings, setExtensionMappings] = useState<
    Record<string, string>
  >(EA_TO_EV2EXPERIMENT_MAPPING);
  const [extensionConfig, setExtensionConfig] =
    useState<ExtensionDetailsType | null>(null);

  // Load local storage mappings
  useEffect(() => {
    const loadMappings = () => {
      try {
        const localStorageMappings = JSON.parse(
          localStorage.getItem(EA_TO_EV2_EXTENSION_MAPPINGS_KEY) || "{}",
        );
        const allMappings = {
          ...EA_TO_EV2EXPERIMENT_MAPPING,
          ...localStorageMappings,
        };
        setExtensionMappings(allMappings);
      } catch (error) {
        console.warn(
          "Failed to load extension mappings from localStorage:",
          error,
        );
        setExtensionMappings(EA_TO_EV2EXPERIMENT_MAPPING);
      }
    };

    const loadExtensionName = () => {
      try {
        const storedExtensionName = localStorage.getItem(
          EXTENSION_KEY_LOCAL_STORAGE,
        );
        if (storedExtensionName) {
          setExtensionName(storedExtensionName);
        }
      } catch (error) {
        console.warn("Failed to load extension name from localStorage:", error);
      }
    };

    loadMappings();
    loadExtensionName();

    // Listen for localStorage changes from other tabs/windows
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === EA_TO_EV2_EXTENSION_MAPPINGS_KEY) {
        loadMappings();
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on mount to avoid infinite loop
  }, []);

  const getNormalizedExtensionName = (extensionName: string) => {
    const normalizedExtension =
      getExtensionMapping(extensionName) || extensionName;
    return normalizedExtension;
  };

  const getNormalizedExtension = (extensionName: string) => {
    const normalizedExtensionName = getNormalizedExtensionName(extensionName);
    const normalizedExtension = extensions.find(
      (extension) => extension.name === normalizedExtensionName,
    );
    return normalizedExtension;
  };

  const isExtensionNameValid = (extensionName: string) => {
    const normalizedExtension = getNormalizedExtensionName(extensionName);
    const isValid = extensions.find(
      (extension) => extension.name === normalizedExtension,
    );
    return !!isValid;
  };

  const setExtensionName = (extensionName: string) => {
    const isValid = isExtensionNameValid(extensionName);
    try {
      if (isValid) {
        localStorage.setItem(EXTENSION_KEY_LOCAL_STORAGE, extensionName);
        setExtensionNameState(extensionName);
      }
    } catch (error) {
      console.warn("Failed to save extension name to localStorage:", error);
      setExtensionNameState(extensionName);
    }
  };

  const updateExtensionMapping = useCallback(
    (fromExtension: string, toExtension: string) => {
      // Update localStorage
      const localStorageMappings = JSON.parse(
        localStorage.getItem(EA_TO_EV2_EXTENSION_MAPPINGS_KEY) || "{}",
      );
      localStorageMappings[fromExtension] = toExtension;
      localStorage.setItem(
        EA_TO_EV2_EXTENSION_MAPPINGS_KEY,
        JSON.stringify(localStorageMappings),
      );
      setExtensionMappings((prev) => ({
        ...prev,
        [fromExtension]: toExtension,
      }));
    },
    [],
  );

  const getExtensionMapping = useCallback(
    (extensionName: string) => {
      return extensionMappings[extensionName];
    },
    [extensionMappings],
  );

  const contextValue: AppContextValue = {
    error,
    setError,
    extensionName: extensionName,
    setExtensionName: setExtensionName,
    extensions,
    setExtensions,
    extensionMappings,
    updateExtensionMapping,
    getExtensionMapping,
    getNormalizedExtensionName,
    getNormalizedExtension,
    isExtensionNameValid,
    extensionConfig,
    setExtensionConfig,
  };

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};

// Custom hook to use the context
export const useAppContext = (): AppContextValue => {
  const context = useContext(AppContext);

  if (context === undefined) {
    throw new Error("useAppContext must be used within an AppProvider");
  }

  return context;
};

// Export the context for advanced usage if needed
export { AppContext };
